import React from "react";
import { Tabs, Badge, Tooltip } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DashboardOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  DollarOutlined,
  CreditCardOutlined,
  UserOutlined,
  SettingOutlined,
} from "@ant-design/icons";

const { TabPane } = Tabs;

interface BillingSubNavProps {
  activeKey?: string;
}

export const BillingSubNav: React.FC<BillingSubNavProps> = ({ activeKey }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveKey = () => {
    if (activeKey) return activeKey;

    const path = location.pathname;
    if (path.includes("/billing/dashboard")) return "dashboard";
    if (path.includes("/billing/invoicing")) return "invoicing";
    if (path.includes("/billing/storage")) return "storage";
    if (path.includes("/billing/sales")) return "sales";
    if (path.includes("/billing/expenses")) return "expenses";
    if (path.includes("/billing/payroll")) return "payroll";
    if (path.includes("/billing/quickbooks")) return "quickbooks";
    return "dashboard";
  };

  const handleTabChange = (key: string) => {
    switch (key) {
      case "dashboard":
        navigate("/billing/dashboard");
        break;
      case "invoicing":
        navigate("/billing/invoicing");
        break;
      case "storage":
        navigate("/billing/storage");
        break;
      case "sales":
        navigate("/billing/sales");
        break;
      case "expenses":
        navigate("/billing/expenses");
        break;
      case "payroll":
        navigate("/billing/payroll");
        break;
      case "quickbooks":
        navigate("/billing/quickbooks");
        break;
      default:
        navigate("/billing/dashboard");
    }
  };

  const currentKey = getActiveKey();

  const TabButton: React.FC<{
    icon: React.ReactNode;
    label: string;
    isActive: boolean;
    color: string;
    tooltip: string;
    badge?: number;
  }> = ({ icon, label, isActive, color, tooltip, badge }) => (
    <Tooltip title={tooltip}>
      <div
        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 cursor-pointer ${
          isActive
            ? `bg-white shadow-md border border-${color}-200 text-${color}-600`
            : `hover:bg-white/50 text-gray-600 hover:text-${color}-600`
        }`}
      >
        <span className="text-lg">{icon}</span>
        <span className="font-medium">{label}</span>
        {badge && badge > 0 && (
          <Badge
            count={badge}
            size="small"
            style={{ backgroundColor: "#10b981" }}
          />
        )}
        {label === "QuickBooks" && (
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
        )}
      </div>
    </Tooltip>
  );

  return (
    <div className="bg-gradient-to-r from-slate-50 via-blue-50 to-indigo-50 border-b border-gray-200 shadow-sm sticky top-[76px] z-10 mb-4 rounded">
      <div className="max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <div className="text-sm text-gray-500 bg-white/50 px-3 py-1 rounded-full">
            🔗 QuickBooks Connected
          </div>
        </div>

        <Tabs
          activeKey={currentKey}
          onChange={handleTabChange}
          className="billing-subnav-tabs"
          size="large"
          tabBarStyle={{
            marginBottom: 0,
            borderBottom: "none",
            background: "transparent",
          }}
        >
          <TabPane
            tab={
              <TabButton
                icon={<DashboardOutlined />}
                label="Dashboard"
                isActive={currentKey === "dashboard"}
                color="blue"
                tooltip="Overview and analytics"
              />
            }
            key="dashboard"
          />
          <TabPane
            tab={
              <TabButton
                icon={<FileTextOutlined />}
                label="Invoicing"
                isActive={currentKey === "invoicing"}
                color="green"
                tooltip="Create and manage invoices"
              />
            }
            key="invoicing"
          />
          <TabPane
            tab={
              <TabButton
                icon={<DatabaseOutlined />}
                label="Storage"
                isActive={currentKey === "storage"}
                color="purple"
                tooltip="Storage billing and calculations"
              />
            }
            key="storage"
          />
          <TabPane
            tab={
              <TabButton
                icon={<DollarOutlined />}
                label="Sales"
                isActive={currentKey === "sales"}
                color="emerald"
                tooltip="Sales reports and analytics"
              />
            }
            key="sales"
          />
          <TabPane
            tab={
              <TabButton
                icon={<CreditCardOutlined />}
                label="Expenses"
                isActive={currentKey === "expenses"}
                color="orange"
                tooltip="Expense tracking and management"
              />
            }
            key="expenses"
          />
          <TabPane
            tab={
              <TabButton
                icon={<UserOutlined />}
                label="Payroll"
                isActive={currentKey === "payroll"}
                color="cyan"
                tooltip="Payroll and staff compensation"
              />
            }
            key="payroll"
          />
          <TabPane
            tab={
              <TabButton
                icon={<SettingOutlined />}
                label="QuickBooks"
                isActive={currentKey === "quickbooks"}
                color="indigo"
                tooltip="QuickBooks integration settings"
              />
            }
            key="quickbooks"
          />
        </Tabs>
      </div>
    </div>
  );
};

export default BillingSubNav;
